import { useEffect, useMemo, useState } from 'react';
import { X, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { stockApi, ventasApi, busesApi, cajaChicaApi, comprasRepuestosApi } from '../lib/api';
import { topeVenta } from '../lib/autorizacionKit';
import { coincideProducto } from '../lib/buscar';

// "+ Agregar fila" del Libro de caja (2026-10-20, pedido de Milenko/Rogelio: digitar como en
// el Excel). No inventa movimientos nuevos: usa las mismas rutas de siempre para que stock,
// deudas y cajas sigan cuadrando --
//   Venta  -> /api/ventas/carrito al contado (descuenta stock, entra a Caja Repuestos)
//   Gasto  -> /api/caja-chica (gasto de Caja Repuestos)
//   Compra -> /api/compras-repuestos (sale de Caja Repuestos, opcional sumar al stock)
//   Pago de deuda -> pantalla Creditos (reparte entre buses y separa la parte de servicio)
type Tipo = 'venta' | 'gasto' | 'compra' | 'deuda';
interface Linea { id: string; nombre: string; unidad?: string; stock: number; cantidad: string; precio: string; precio_lista: number; precio_compra?: number | null }

interface Props {
  onClose: () => void;
  onGuardado: () => void;
}

const num = (s: string) => parseFloat(String(s).replace(',', '.')) || 0;
// Busqueda sin tildes ni mayusculas: "bujia" encuentra "Bujía"

const FilaLibroModal = ({ onClose, onGuardado }: Props) => {
  const navigate = useNavigate();
  const [tipo, setTipo] = useState<Tipo>('venta');
  const [guardando, setGuardando] = useState(false);
  const [productos, setProductos] = useState<any[]>([]);
  const [buses, setBuses] = useState<any[]>([]);
  const [busqueda, setBusqueda] = useState('');

  // Venta
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [padron, setPadron] = useState('');
  const [aNombreDe, setANombreDe] = useState('');
  const [metodo, setMetodo] = useState<'efectivo' | 'yape'>('efectivo');
  // Gasto
  const [gastoDesc, setGastoDesc] = useState('');
  const [gastoMonto, setGastoMonto] = useState('');
  // Compra
  const [compraDesc, setCompraDesc] = useState('');
  const [compraCant, setCompraCant] = useState('1');
  const [compraMonto, setCompraMonto] = useState('');
  const [compraProv, setCompraProv] = useState('');
  const [compraProducto, setCompraProducto] = useState<any>(null);

  useEffect(() => {
    stockApi.getFull().then(d => setProductos((d || []).filter((p: any) => p.estado === 'activo' && p.tipo !== 'herramienta'))).catch(() => {});
    busesApi.getAll().then(d => setBuses(d || [])).catch(() => {});
  }, []);

  const encontrados = useMemo(() => {
    if (!busqueda.trim()) return [];
    return productos.filter(p => coincideProducto(busqueda, p)).slice(0, 8);
  }, [busqueda, productos]);

  const agregarLinea = (p: any) => {
    setBusqueda('');
    if (tipo === 'compra') { setCompraProducto(p); if (!compraDesc) setCompraDesc(p.nombre); return; }
    if (lineas.some(l => l.id === p.id)) return toast.error('Ese producto ya está en la venta');
    setLineas([...lineas, { id: p.id, nombre: p.nombre, unidad: p.unidad, stock: topeVenta(p), cantidad: '1',
      precio: String(p.precio_venta || p.precio || 0), precio_lista: Number(p.precio_venta || p.precio || 0), precio_compra: p.precio_compra ?? null }]);
  };
  const cambiarLinea = (i: number, campo: 'cantidad' | 'precio', v: string) =>
    setLineas(lineas.map((l, j) => (j === i ? { ...l, [campo]: v } : l)));
  const totalVenta = lineas.reduce((t, l) => t + num(l.cantidad) * num(l.precio), 0);
  // 2026-10-22: precio distinto al de lista (si el producto tiene precio) -> clave de administrador
  const preciosCambiados = lineas.some(l => l.precio_lista > 0 && Math.abs(num(l.precio) - l.precio_lista) > 0.005);
  const [autMotivo, setAutMotivo] = useState('');
  const [autClave, setAutClave] = useState('');

  const guardar = async () => {
    try {
      if (tipo === 'venta') {
        if (!lineas.length) return toast.error('Agrega al menos un producto');
        for (const l of lineas) {
          if (num(l.cantidad) <= 0) return toast.error(`Cantidad inválida en ${l.nombre}`);
          if (num(l.cantidad) > l.stock) return toast.error(`No alcanza el stock de ${l.nombre} (hay ${l.stock})`);
          if (num(l.precio) < 0) return toast.error(`Precio inválido en ${l.nombre}`);
        }
        const p = padron.trim();
        if (p && !buses.some(b => String(b.padron) === p)) return toast.error(`No existe un bus con padrón ${p}`);
        if (preciosCambiados && (!autMotivo.trim() || !autClave)) return toast.error('Cambiaste un precio: escribe el motivo y la clave de administrador');
        setGuardando(true);
        const total = Math.round(totalVenta * 100) / 100;
        const venta = await ventasApi.crearConCarrito({
          cliente_nombre: !p && aNombreDe.trim() ? aNombreDe.trim().slice(0, 80) : 'Cliente Contado',
          placa_bus: p || null,
          tipo_pago: metodo,
          items: lineas.map(l => ({ id: l.id, nombre: l.nombre, cantidad: num(l.cantidad), precio: num(l.precio), precio_compra: l.precio_compra })),
          subtotal: total, igv: 0, total,
          cerrada: true,
          autorizacion_precio: preciosCambiados ? { clave: autClave, motivo: autMotivo.trim() } : undefined,
          observaciones: 'Libro de caja',
        });
        toast.success(`Venta ${venta.numero} registrada`);
      } else if (tipo === 'gasto') {
        if (!gastoDesc.trim()) return toast.error('Escribe en qué se gastó');
        if (num(gastoMonto) <= 0) return toast.error('El monto debe ser mayor a 0');
        setGuardando(true);
        await cajaChicaApi.create({ tipo: 'gasto', monto: num(gastoMonto), descripcion: gastoDesc.trim(), categoria: 'Otro', caja: 'ventas' });
        toast.success('Gasto registrado');
      } else if (tipo === 'compra') {
        if (!compraDesc.trim()) return toast.error('Escribe qué repuesto se compró');
        if (num(compraCant) <= 0) return toast.error('La cantidad debe ser mayor a 0');
        if (num(compraMonto) <= 0) return toast.error('El monto debe ser mayor a 0');
        setGuardando(true);
        await comprasRepuestosApi.create({
          descripcion: compraDesc.trim(), cantidad: num(compraCant), monto: num(compraMonto), proveedor: compraProv.trim() || null,
          metodo_pago: 'efectivo', producto_id: compraProducto?.id || null, agregar_stock: !!compraProducto,
        });
        toast.success(compraProducto ? 'Compra registrada y sumada al stock' : 'Compra registrada');
      }
      onGuardado();
      onClose();
    } catch (e: any) {
      toast.error(e.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const pestaña = (t: Tipo, texto: string, color: string) => (
    <button onClick={() => { setTipo(t); setBusqueda(''); }}
      className={`flex-1 px-2 py-2 rounded-lg text-sm font-semibold border ${tipo === t ? color + ' text-white border-transparent' : 'bg-white hover:bg-gray-50'}`}>
      {texto}
    </button>
  );

  const buscador = (placeholder: string) => (
    <div className="relative">
      <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 border rounded-lg" />
      {encontrados.length > 0 && (
        <div className="absolute z-10 left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
          {encontrados.map(p => (
            <button key={p.id} onClick={() => agregarLinea(p)} className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-0 text-sm">
              <div className="font-medium">{p.nombre}{p.es_kit && <span className="ml-2 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-semibold">KIT</span>}</div>
              <div className="text-xs text-gray-500">
                {p.es_kit ? 'Kits armables' : 'Stock'}: {Number(p.stock) || 0}{p.unidad === 'metro' ? ' m' : ''} · S/ {Number(p.precio_venta || p.precio || 0).toFixed(2)}{p.codigo ? ` · ${p.codigo}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-bold">Agregar fila al libro</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={22} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">ENTRADA</div>
            <div className="flex gap-2">
              {pestaña('venta', 'Venta', 'bg-green-600')}
              {pestaña('deuda', 'Pago de deuda', 'bg-green-600')}
            </div>
            <div className="text-xs font-semibold text-gray-500 mt-3 mb-1">SALIDA</div>
            <div className="flex gap-2">
              {pestaña('gasto', 'Gasto', 'bg-red-600')}
              {pestaña('compra', 'Compra de repuestos', 'bg-red-600')}
            </div>
          </div>

          {tipo === 'venta' && (
            <div className="space-y-3">
              {buscador('Buscar producto del stock (nombre o código)...')}
              {lineas.length > 0 && (
                <div className="border rounded-lg divide-y">
                  {lineas.map((l, i) => (
                    <div key={l.id} className="p-2 text-sm">
                      <div className="flex justify-between gap-2">
                        <span className="font-medium">{l.nombre}</span>
                        <button onClick={() => setLineas(lineas.filter((_, j) => j !== i))} className="text-red-600"><Trash2 size={16} /></button>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <label className="text-xs text-gray-500">Cant.{l.unidad === 'metro' ? ' (m)' : ''}</label>
                        <input inputMode="decimal" value={l.cantidad} onChange={e => cambiarLinea(i, 'cantidad', e.target.value)} className="w-16 px-2 py-1 border rounded" />
                        <label className="text-xs text-gray-500">Precio S/</label>
                        <input inputMode="decimal" value={l.precio} onChange={e => cambiarLinea(i, 'precio', e.target.value)} className="w-20 px-2 py-1 border rounded" />
                        <span className="ml-auto font-semibold">S/ {(num(l.cantidad) * num(l.precio)).toFixed(2)}</span>
                      </div>
                      <div className={`text-xs mt-0.5 ${num(l.cantidad) > l.stock ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>Stock disponible: {l.stock}</div>
                    </div>
                  ))}
                  {preciosCambiados && (
                    <div className="bg-amber-50 border border-amber-300 rounded-lg p-2 space-y-2 mt-2">
                      <p className="text-xs text-amber-900 font-medium">Cambiaste un precio de lista: se necesita la clave de administrador (queda en Auditoría).</p>
                      <input value={autMotivo} onChange={e => setAutMotivo(e.target.value)} maxLength={200} placeholder="Motivo del cambio de precio" className="w-full px-2 py-1 border rounded text-sm" />
                      <input type="password" value={autClave} onChange={e => setAutClave(e.target.value)} autoComplete="off" placeholder="Clave de administrador" className="w-full px-2 py-1 border rounded text-sm" />
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500">Bus (padrón, opcional)</label>
                  <input list="libro-buses" value={padron} onChange={e => setPadron(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                  <datalist id="libro-buses">{buses.map(b => <option key={b.id} value={b.padron}>{b.dueno || ''}</option>)}</datalist>
                </div>
                <div>
                  <label className="text-xs text-gray-500">A nombre de (opcional)</label>
                  <input value={aNombreDe} onChange={e => setANombreDe(e.target.value)} disabled={!!padron.trim()} placeholder={padron.trim() ? 'Se usa el dueño del bus' : 'Ej: pacheco'} className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-100" />
                </div>
              </div>
              <div className="flex gap-2">
                {(['efectivo', 'yape'] as const).map(m => (
                  <button key={m} onClick={() => setMetodo(m)} className={`flex-1 py-2 rounded-lg border text-sm font-semibold ${metodo === m ? (m === 'yape' ? 'bg-purple-600' : 'bg-gray-700') + ' text-white' : 'bg-white'}`}>
                    {m === 'yape' ? 'Yape / Plin' : 'Efectivo'}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total (entrada)</span><span className="text-green-700">S/ {totalVenta.toFixed(2)}</span></div>
              <p className="text-xs text-gray-500">Venta al contado: descuenta el stock y entra a Caja Repuestos. Las ventas a crédito se hacen en Ventas.</p>
            </div>
          )}

          {tipo === 'deuda' && (
            <div className="space-y-3 text-sm">
              <p>El pago de deuda se registra en <b>Créditos</b>: ahí se elige el dueño, se reparte entre sus buses y la parte de servicio pasa sola a Caja Servicios. Al terminar, el pago aparece en este libro como entrada.</p>
              <button onClick={() => navigate('/creditos')} className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold">Ir a Cobrar deuda</button>
            </div>
          )}

          {tipo === 'gasto' && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Descripción</label>
                <input value={gastoDesc} onChange={e => setGastoDesc(e.target.value)} placeholder="Ej: Taxi movilidad" className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Monto S/ (salida)</label>
                <input inputMode="decimal" value={gastoMonto} onChange={e => setGastoMonto(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <p className="text-xs text-gray-500">Sale de Caja Repuestos. Si es una compra de repuestos, usa "Compra de repuestos".</p>
            </div>
          )}

          {tipo === 'compra' && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">¿Sumar al stock? Busca el producto (opcional)</label>
                {compraProducto ? (
                  <div className="flex justify-between items-center px-3 py-2 border rounded-lg bg-blue-50 text-sm">
                    <span>Se suma al stock de <b>{compraProducto.nombre}</b></span>
                    <button onClick={() => setCompraProducto(null)} className="text-red-600"><X size={16} /></button>
                  </div>
                ) : buscador('Buscar producto para sumarlo al stock...')}
              </div>
              <div>
                <label className="text-xs text-gray-500">Qué se compró</label>
                <input value={compraDesc} onChange={e => setCompraDesc(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-gray-500">Cantidad</label>
                  <input inputMode="decimal" value={compraCant} onChange={e => setCompraCant(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500">Monto total S/ (salida)</label>
                  <input inputMode="decimal" value={compraMonto} onChange={e => setCompraMonto(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500">Proveedor (opcional)</label>
                <input value={compraProv} onChange={e => setCompraProv(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
            </div>
          )}
        </div>
        {tipo !== 'deuda' && (
          <div className="flex gap-2 p-4 border-t">
            <button onClick={onClose} disabled={guardando} className="flex-1 py-2 bg-gray-200 rounded-lg">Cancelar</button>
            <button onClick={guardar} disabled={guardando}
              className={`flex-1 py-2 rounded-lg text-white font-semibold flex items-center justify-center gap-1 disabled:opacity-60 ${tipo === 'venta' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
              <Plus size={16} /> {guardando ? 'Guardando...' : 'Guardar fila'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilaLibroModal;
